import { useState, useEffect, useCallback } from 'react';
import { userDataService, DiveLocation } from '../services/userDataService';
import { preferencesManager } from '../utils/preferencesManager';
import { findNearbyLocation } from '../utils/nearbyLocation';

export const useDiveLocations = () => {
  const [locations, setLocations] = useState<Map<string, DiveLocation>>(new Map());
  const [locationsVersion, setLocationsVersion] = useState(0); // Track changes to locations
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = preferencesManager.getUserId();

  // Load all dive locations for the user
  const loadLocations = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const userLocations = await userDataService.getDiveLocations(userId);
      const locationMap = new Map<string, DiveLocation>();

      userLocations.forEach(loc => {
        locationMap.set(loc.diveKey, loc);
      });

      setLocations(locationMap);
      setLocationsVersion(prev => prev + 1);
    } catch (err) {
      console.error('Failed to load dive locations:', err);
      setError('Failed to load dive locations');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Get location for a specific dive, with optional coordinate fallback.
  // Fallback lets a dive inherit the name of a saved location within
  // NEARBY_LOCATION_RADIUS_KM, since GPS fixes for one site rarely match exactly.
  const getLocation = useCallback((diveKey: string, latitude?: number, longitude?: number): DiveLocation | undefined => {
    const directMatch = locations.get(diveKey);
    if (directMatch?.isUserEdited === true) {
      return directMatch;
    }

    if (latitude == null || longitude == null) {
      return directMatch;
    }

    return findNearbyLocation(locations.values(), latitude, longitude) ?? directMatch;
  }, [locations]);

  // Save or update a dive location
  const saveLocation = async (
    latitude: number,
    longitude: number,
    diveDate: Date,
    locationName?: string,
    notes?: string,
    isUserEdited?: boolean
  ): Promise<void> => {
    if (!userId) {
      throw new Error('User ID not set');
    }

    const diveKey = userDataService.generateDiveKey(latitude, longitude, diveDate);

    const location: DiveLocation = {
      userId,
      diveKey,
      latitude,
      longitude,
      locationName,
      isUserEdited,
      notes,
      diveDate: diveDate.toISOString(),
      updatedAt: new Date().toISOString(),
    };

    try {
      await userDataService.saveDiveLocation(location);

      // Update local state
      setLocations(prev => new Map(prev).set(diveKey, location));
      setLocationsVersion(prev => prev + 1);
    } catch (err) {
      console.error('❌ Failed to save dive location:', err);
      throw err;
    }
  };

  // Delete a dive location override
  const deleteLocation = async (diveKey: string): Promise<void> => {
    if (!userId) {
      throw new Error('User ID not set');
    }

    try {
      await userDataService.deleteDiveLocation(userId, diveKey);

      // Update local state
      setLocations(prev => {
        const newMap = new Map(prev);
        newMap.delete(diveKey);
        return newMap;
      });
      setLocationsVersion(prev => prev + 1);
    } catch (err) {
      console.error('Failed to delete dive location:', err);
      throw err;
    }
  };

  // Load locations when userId changes
  useEffect(() => {
    if (userId) {
      loadLocations();
    }
  }, [userId, loadLocations]);

  return {
    locations,
    locationsVersion, // Expose version for effects to track changes
    loading,
    error,
    getLocation,
    saveLocation,
    deleteLocation,
    reloadLocations: loadLocations,
  };
};
